/**
 * UnifiedClientPortal - Combined Document Upload + Transaction Clarification
 * 
 * Single portal for clients to complete ALL requested items in one session.
 * Accessed via magic link: /portal/{token}
 * 
 * Features:
 * - Tabbed interface: Documents | Clarifications
 * - Unified progress tracking across both
 * - One "Submit All" when everything complete
 * - Session persistence (auto-save)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Upload,
  FileText,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  CloudUpload,
  Shield,
  Clock,
  File,
  Mail,
  Info,
  ChevronDown,
  Check,
  Layers,
  Copy,
  HelpCircle,
  Send,
  FileCheck,
  ListChecks,
  BarChart3
} from 'lucide-react';
import { api } from '../utils/api';
import { useUnifiedUpload, FileUploadState } from '../hooks/useUnifiedUpload';
import UnifiedUploadProgress from '../components/upload/UnifiedUploadProgress';
import EvidenceUploadSlots from '../components/EvidenceUploadSlots';
import { FinancialValue } from '../components/shared/FinancialValue';
import { formatCurrency } from '../utils/financialFormat';
import { useClientPortalContext, portalDisplayName } from '../layouts/ClientPortalContext';

// Feature flag for new unified upload pipeline
const USE_UNIFIED_UPLOAD = true;

// Feature flag for evidence-based upload slots (dynamic from AI checklist)
// Set to true when EvidenceUploadSlots component is ready
const USE_EVIDENCE_SLOTS = true;

// Temporarily disable useUnifiedUpload hook to fix initialization error
const DISABLE_UNIFIED_UPLOAD_HOOK = true;

const MISSING_TOKEN_MESSAGE =
  'This link is missing its access token. Please open the full link from your email, or contact your representative for a new one.';

// File limits
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Document type icons
const FILE_ICONS: Record<string, React.ReactNode> = {
  'application/pdf': <FileText className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />,
  'text/csv': <FileSpreadsheet className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': <FileSpreadsheet className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />,
  'application/vnd.ms-excel': <FileSpreadsheet className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />,
};

// Interfaces
interface MissingDoc {
  name: string;
  reason: string;
}

interface Category {
  value: string;
  label: string;
  type: string;
}

interface Transaction {
  transaction_id: string;
  date: string;
  description: string;
  amount: number;
  current_category?: string;
  question: string;
  options?: string[];
  client_category?: string;
  client_notes?: string;
  ai_suggested_category?: string;  // AI's recommended category
  source?: string;  // 'qa_assistant' if AI-generated
}

interface TransactionResponse {
  transaction_id: string;
  selected_category: string;
  new_category_name?: string;
  notes?: string;
}

interface PortalData {
  // Common
  claimant_name: string;
  client_name?: string;
  report_id: string;
  status: string;
  // Optional aliases used by EvidenceUploadSlots prop wiring at L925-926.
  // Backend serializers may return either `claim_id` or `draft_id`
  // depending on the call site. Restored per archaeology pattern §1.
  claim_id?: string;
  draft_id?: string;
  
  // Documents
  has_documents: boolean;
  missing_docs: MissingDoc[];
  inbound_email?: string;
  // Server-side document + progress numbers (one source shared with /status)
  documents_total?: number | null;
  documents_received?: number | null;
  progress_percent?: number | null;
  
  // Clarifications
  has_clarifications: boolean;
  request_id?: string;
  transactions: Transaction[];
  categories: Category[];
  can_resubmit?: boolean;
  clarification_status?: string;
  
  // AI Questions
  ai_questions_count?: number;
  has_ai_questions?: boolean;
}

// Helper to find similar transactions
const findSimilarTransactions = (txn: Transaction, allTransactions: Transaction[]): Transaction[] => {
  const description = txn.description.toLowerCase();
  const skipWords = new Set(['the', 'and', 'for', 'inc', 'llc', 'ltd', 'co', 'corp', 'payment', 'transfer']);
  const keyWords = description
    .split(/[\s\-_\/\\]+/)
    .filter(word => word.length > 2 && !skipWords.has(word))
    .slice(0, 3);
  
  if (keyWords.length === 0) return [];
  
  return allTransactions.filter(t => {
    if (t.transaction_id === txn.transaction_id) return false;
    const otherDesc = t.description.toLowerCase();
    return keyWords.some(word => otherDesc.includes(word));
  });
};

export default function UnifiedClientPortal() {
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const portalCtx = useClientPortalContext();
  const token = portalCtx?.token ?? pathToken ?? searchParams.get('token') ?? undefined;
  const initialTab = searchParams.get('tab') || 'overview';

  // Inside ClientPortalLayout (/client routes) the layout owns the single GET /portal/{token}
  // and the page header/progress; this component then renders embedded and never fetches.
  const embedded = portalCtx !== null;

  // Core state
  const [localLoading, setLocalLoading] = useState(Boolean(token));
  const [localError, setLocalError] = useState<string | null>(null);
  const [localData, setLocalData] = useState<PortalData | null>(null);
  const data: PortalData | null = embedded ? (portalCtx!.data as PortalData | null) : localData;
  const setError = setLocalError;
  const error = localError ?? (token ? null : MISSING_TOKEN_MESSAGE);
  const loading = embedded ? Boolean(token) && !data : localLoading;
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'clarifications'>('overview');
  
  // Document upload state - NEW unified upload
  const [pendingFiles, setPendingFiles] = useState<File[]>([]); // Files waiting to be uploaded
  const [isDragging, setIsDragging] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [showUnifiedProgress, setShowUnifiedProgress] = useState(false);
  
  // Unified Upload Hook - batch-enabled
  // Pass report_id from portal data to link uploads to the correct case
  const {
    files: unifiedFiles,
    isUploading,
    uploadFiles,
    clearFiles: clearUnifiedFiles,
    allComplete,
    anyFailed,
    totalTransactions,
    summary: uploadSummary,
    completedCount,
    retryFile,  // Manual retry function
  } = useUnifiedUpload({
    uploaderType: 'client',
    claimId: data?.report_id,  // ← CRITICAL: Link uploads to the case/report
    portalToken: token,         // Magic token — backend accepts this instead of Clerk JWT
    autoRetryOnFailure: true,  // Auto-retry failed files once
    onAllComplete: (files) => {
      console.log('✅ Client portal upload complete:', files);
      console.log(`📊 Report ID: ${data?.report_id}`);
      const successCount = files.filter(f => f.status === 'parsed').length;
      console.log(`📊 ${successCount}/${files.length} files processed`);
    }
  });
  
  // Clarification state
  const [responses, setResponses] = useState<Record<string, TransactionResponse>>({});
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [bulkApplyModal, setBulkApplyModal] = useState<{
    isOpen: boolean;
    sourceTransaction: Transaction | null;
    similarTransactions: Transaction[];
    selectedCategory: string;
  }>({ isOpen: false, sourceTransaction: null, similarTransactions: [], selectedCategory: '' });
  const [bulkApplySuccess, setBulkApplySuccess] = useState<string | null>(null);
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Inbound email
  const inboundEmail = data?.report_id ? `docs+${data.report_id}@updates.claimsync.io` : '';
  
  // Track uploaded count from unified hook
  const uploadedCount = completedCount;

  // Seed the tab + saved clarification answers from freshly loaded portal data.
  // Runs once per load; a quiet refresh (after an upload) must not reset the client's
  // current tab or unsaved selections.
  const applyInitialPortalState = useCallback((result: PortalData) => {
    if (result.transactions) {
      const initialResponses: Record<string, TransactionResponse> = {};
      result.transactions.forEach((txn: Transaction) => {
        if (txn.client_category) {
          initialResponses[txn.transaction_id] = {
            transaction_id: txn.transaction_id,
            selected_category: txn.client_category,
            notes: txn.client_notes
          };
        }
      });
      setResponses(initialResponses);
    }

    if (result.has_documents && !result.has_clarifications) {
      setActiveTab('documents');
    } else if (result.has_clarifications && !result.has_documents) {
      setActiveTab('clarifications');
    } else {
      setActiveTab('overview');
    }
  }, []);

  const initialStateApplied = React.useRef(false);

  // Standalone (/portal/:token): fetch here. Embedded: the layout already did.
  const fetchPortalData = useCallback(async (quiet = false) => {
    if (embedded) {
      await portalCtx!.refresh();
      return;
    }
    try {
      if (!quiet) setLocalLoading(true);
      setLocalError(null);
      const result = await api.get(`/api/v1/portal/${token}`, { showErrorToast: false });
      setLocalData(result);
      if (!initialStateApplied.current) {
        initialStateApplied.current = true;
        applyInitialPortalState(result);
      }
    } catch (err: any) {
      if (quiet) return; // keep what is already on screen
      if (err.message?.includes('404')) {
        setLocalError('This link is invalid or has expired. Please contact your representative for a new link.');
      } else if (err.message?.includes('410')) {
        setLocalError('This link has expired. Please contact your representative for a new link.');
      } else {
        setLocalError(err.message || 'Failed to load portal');
      }
    } finally {
      if (!quiet) setLocalLoading(false);
    }
  }, [embedded, portalCtx, token, applyInitialPortalState]);

  useEffect(() => {
    if (embedded) return;
    if (token) {
      fetchPortalData();
    } else {
      setLocalLoading(false); // no token: show the missing-token error, never a permanent spinner
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, embedded]);

  // Embedded: seed state from the layout's data the first time it arrives.
  useEffect(() => {
    if (embedded && data && !initialStateApplied.current) {
      initialStateApplied.current = true;
      applyInitialPortalState(data);
    }
  }, [embedded, data, applyInitialPortalState]);

  // ═══════════════════════════════════════════════════════════════════════════
  // DOCUMENT UPLOAD HANDLERS - NEW UNIFIED PIPELINE
  // ═══════════════════════════════════════════════════════════════════════════

  // Validate and add files to pending list
  const addFiles = useCallback((newFiles: File[]) => {
    const validTypes = [
      'application/pdf',
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const validFiles = newFiles.filter(file => {
      const isValidType = validTypes.includes(file.type) || file.name.endsWith('.csv');
      const isValidSize = file.size <= MAX_FILE_SIZE_BYTES;
      return isValidType && isValidSize;
    });

    if (validFiles.length < newFiles.length) {
      const invalidCount = newFiles.length - validFiles.length;
      console.warn(`⚠️ ${invalidCount} files skipped (invalid type or size)`);
    }

    setPendingFiles(prev => [...prev, ...validFiles]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, [addFiles]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      addFiles(selectedFiles);
    }
  };

  const guessDocType = (filename: string): string => {
    const lower = filename.toLowerCase();
    if (lower.includes('1065') || lower.includes('tax') || lower.includes('return')) return 'Tax Returns';
    if (lower.includes('k-1') || lower.includes('k1')) return 'K-1 Forms';
    if (lower.includes('bank') || lower.includes('statement')) return 'Bank Statements';
    if (lower.includes('p&l') || lower.includes('pnl') || lower.includes('profit')) return 'P&L Statements';
    if (lower.includes('sales') || lower.includes('revenue')) return 'Sales Reports';
    if (lower.includes('payroll')) return 'Payroll Records';
    if (lower.includes('invoice')) return 'Invoices';
    return 'Financial Document';
  };

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload all pending files using the unified batch endpoint
  const uploadAllFiles = useCallback(async () => {
    if (pendingFiles.length === 0) return;
    
    console.log(`📤 Uploading ${pendingFiles.length} files via unified batch endpoint...`);
    setShowUnifiedProgress(true);
    
    // Use the unified upload hook
    uploadFiles(pendingFiles);
    
    // Clear pending files after triggering upload
    setPendingFiles([]);
  }, [pendingFiles, uploadFiles]);

  // Clear all files (pending and uploaded)
  const clearAllFiles = useCallback(() => {
    setPendingFiles([]);
    clearUnifiedFiles();
    setShowUnifiedProgress(false);
  }, [clearUnifiedFiles]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CLARIFICATION HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleCategorySelect = (transactionId: string, category: string) => {
    setResponses(prev => ({
      ...prev,
      [transactionId]: {
        ...prev[transactionId],
        transaction_id: transactionId,
        selected_category: category
      }
    }));
  };

  const handleNewCategoryName = (transactionId: string, name: string) => {
    setResponses(prev => ({
      ...prev,
      [transactionId]: {
        ...prev[transactionId],
        new_category_name: name
      }
    }));
  };

  const handleNotesChange = (transactionId: string, notes: string) => {
    setResponses(prev => ({
      ...prev,
      [transactionId]: {
        ...prev[transactionId],
        notes
      }
    }));
  };

  const toggleNotes = (transactionId: string) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transactionId)) {
        newSet.delete(transactionId);
      } else {
        newSet.add(transactionId);
      }
      return newSet;
    });
  };

  // Bulk apply
  const openBulkApply = (txn: Transaction, category: string) => {
    if (!data) return;
    const similar = findSimilarTransactions(txn, data.transactions);
    const unansweredSimilar = similar.filter(t => !responses[t.transaction_id]?.selected_category);
    if (unansweredSimilar.length === 0) return;
    
    setBulkApplyModal({
      isOpen: true,
      sourceTransaction: txn,
      similarTransactions: unansweredSimilar,
      selectedCategory: category
    });
  };

  const executeBulkApply = () => {
    const { similarTransactions, selectedCategory, sourceTransaction } = bulkApplyModal;
    if (!selectedCategory || similarTransactions.length === 0) return;
    
    const newResponses = { ...responses };
    similarTransactions.forEach(txn => {
      newResponses[txn.transaction_id] = {
        transaction_id: txn.transaction_id,
        selected_category: selectedCategory,
        notes: `Bulk applied from: ${sourceTransaction?.description}`
      };
    });
    
    setResponses(newResponses);
    setBulkApplySuccess(`Applied "${selectedCategory}" to ${similarTransactions.length} similar transactions!`);
    setBulkApplyModal({ isOpen: false, sourceTransaction: null, similarTransactions: [], selectedCategory: '' });
    setTimeout(() => setBulkApplySuccess(null), 3000);
  };

  // Similar counts for bulk apply buttons
  const similarCounts = useMemo(() => {
    if (!data?.transactions) return {};
    const counts: Record<string, number> = {};
    data.transactions.forEach(txn => {
      const similar = findSimilarTransactions(txn, data.transactions);
      const unanswered = similar.filter(t => !responses[t.transaction_id]?.selected_category);
      counts[txn.transaction_id] = unanswered.length;
    });
    return counts;
  }, [data, responses]);

  // ═══════════════════════════════════════════════════════════════════════════
  // SUBMISSION
  // ═══════════════════════════════════════════════════════════════════════════

  const handleSubmitAll = async () => {
    if (!token || !data) return;
    
    // Validate clarifications if present
    if (data.has_clarifications) {
      const unanswered = data.transactions.filter(
        txn => !responses[txn.transaction_id]?.selected_category
      );
      
      if (unanswered.length > 0) {
        setError(`Please categorize all ${unanswered.length} remaining transaction(s) before submitting.`);
        setActiveTab('clarifications');
        return;
      }
      
      const invalidOther = Object.values(responses).filter(
        r => r.selected_category === 'Other' && !r.new_category_name?.trim()
      );
      
      if (invalidOther.length > 0) {
        setError('Please provide a category name for all "Other" selections.');
        setActiveTab('clarifications');
        return;
      }
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Submit clarifications if present
      if (data.has_clarifications && Object.keys(responses).length > 0) {
        const submitToken = portalCtx?.source === 'session' && portalCtx?.clarificationToken
          ? portalCtx.clarificationToken
          : token;
        await api.post(`/api/v1/clarifications/client/${submitToken}/submit`, {
          submissions: Object.values(responses)
        }, { showErrorToast: false });
      }
      
      // Track conversion (fire and forget)
      api.post(`/api/v1/notifications/track/conversion?tracking_id=${token}_unified&action=portal_submitted&details=docs:${uploadedCount},clarify:${Object.keys(responses).length}`, null, { showErrorToast: false }).catch(() => {});
      
      setSubmitted(true);
      
    } catch (err: any) {
      setError(err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PROGRESS CALCULATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  const docsProgress = useMemo(() => {
    if (!data?.has_documents) return { done: 0, total: 0, percent: 100 };
    if (data.documents_total != null) {
      // Server numbers: same source as the Documents tab and /status, survive a reload.
      const total = data.documents_total;
      const done = Math.min(data.documents_received ?? 0, total);
      return { done, total, percent: total > 0 ? Math.round((done / total) * 100) : 100 };
    }
    const total = data.missing_docs?.length || 0;
    const done = uploadedCount;
    return { done, total, percent: total > 0 ? Math.round((done / total) * 100) : 100 };
  }, [data, uploadedCount]);

  const clarifyProgress = useMemo(() => {
    if (!data?.has_clarifications) return { done: 0, total: 0, percent: 100 };
    const total = data.transactions?.length || 0;
    const done = Object.keys(responses).filter(id => responses[id]?.selected_category).length;
    return { done, total, percent: total > 0 ? Math.round((done / total) * 100) : 100 };
  }, [data, responses]);

  const localOverallProgress = useMemo(() => {
    const docsWeight = data?.has_documents ? 1 : 0;
    const clarifyWeight = data?.has_clarifications ? 1 : 0;
    const totalWeight = docsWeight + clarifyWeight;
    if (totalWeight === 0) return 100;
    
    const docsContrib = docsWeight * (docsProgress.percent / 100);
    const clarifyContrib = clarifyWeight * (clarifyProgress.percent / 100);
    return Math.round(((docsContrib + clarifyContrib) / totalWeight) * 100);
  }, [data, docsProgress, clarifyProgress]);

  // Embedded: the layout header shows the server's progress_percent, so the footer must too.
  const overallProgress = embedded && data?.progress_percent != null
    ? Math.round(data.progress_percent)
    : localOverallProgress;
  // Documents-uploaded display: server count when embedded and present, else the session count.
  const docsUploadedShown = embedded && data?.documents_received != null
    ? data.documents_received
    : uploadedCount;

  const canSubmit = useMemo(() => {
    // Must have uploaded at least some files if docs needed OR all clarifications done
    const docsOk = !data?.has_documents || uploadedCount > 0 || unifiedFiles.some(f => f.status === 'parsed');
    const clarifyOk = !data?.has_clarifications || clarifyProgress.percent === 100;
    return docsOk || clarifyOk;
  }, [data, uploadedCount, unifiedFiles, clarifyProgress]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER STATES
  // ═══════════════════════════════════════════════════════════════════════════

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
          <p className="cs-body">Loading your portal...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div className="max-w-md w-full rounded-2xl shadow-xl p-8 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--color-needs-human-tint)' }}>
            <AlertCircle className="w-8 h-8" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <h1 className="cs-h2 mb-2">Link Error</h1>
          <p className="cs-body mb-6">{error}</p>
          {token && (
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 rounded-lg cs-label transition-colors"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  // Success - All submitted
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div className="max-w-md w-full rounded-2xl shadow-xl p-8 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'transparent' }}>
            <CheckCircle2 className="w-10 h-10" style={{ color: 'var(--color-verified)' }} />
          </div>
          <h1 className="cs-display mb-3">Thank You!</h1>
          <p className="cs-body mb-6">
            Your information has been submitted successfully. A data specialist will review everything shortly.
          </p>

          <div className="rounded-lg p-4 text-left mb-6" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-sub)' }}>
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
              <div className="cs-body">
                <p className="mb-1" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>What happens next?</p>
                <p className="cs-body">
                  Our team will incorporate your responses into your claim analysis. You'll be contacted if we need any additional information.
                </p>
              </div>
            </div>
          </div>

          <div className="cs-meta space-y-1">
            {docsUploadedShown > 0 && <p>✓ {docsUploadedShown} document(s) uploaded</p>}
            {Object.keys(responses).length > 0 && <p>✓ {Object.keys(responses).length} transaction(s) categorized</p>}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN PORTAL UI
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg-base)' }}>
      {/* Bulk Apply Modal */}
      {bulkApplyModal.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="rounded-2xl shadow-2xl max-w-lg w-full p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }} data-testid="bulk-apply-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                <Layers className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              </div>
              <div>
                <h3 className="cs-h3">Apply to Similar Transactions</h3>
                <p className="cs-meta">
                  Found {bulkApplyModal.similarTransactions.length} similar transactions
                </p>
              </div>
            </div>

            <div className="mb-4 p-3 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
              <p className="cs-meta mb-1">Category to apply:</p>
              <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{bulkApplyModal.selectedCategory}</p>
            </div>

            <div className="mb-4">
              <p className="cs-label mb-2">Will be applied to:</p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {bulkApplyModal.similarTransactions.map(txn => (
                  <div key={txn.transaction_id} className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                    <div className="flex-1 min-w-0">
                      <p className="cs-body truncate" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{txn.description}</p>
                      <p className="cs-meta">{txn.date} • <span className="cs-figure"><FinancialValue value={txn.amount} cents /></span></p>
                    </div>
                    <Check className="w-4 h-4 flex-shrink-0 ml-2" style={{ color: 'var(--color-brand)' }} />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setBulkApplyModal({ isOpen: false, sourceTransaction: null, similarTransactions: [], selectedCategory: '' })}
                className="flex-1 px-4 py-2 rounded-lg cs-label"
                style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', background: 'transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                Cancel
              </button>
              <button
                onClick={executeBulkApply}
                data-testid="confirm-bulk-apply"
                className="flex-1 px-4 py-2 rounded-lg cs-label flex items-center justify-center gap-2"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
              >
                <Copy className="w-4 h-4" />
                Apply to All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Apply Success Toast */}
      {bulkApplySuccess && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-slide-in" style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)' }}>
          <CheckCircle2 className="w-5 h-5" />
          <span className="cs-body" style={{ color: 'var(--color-on-brand)' }}>{bulkApplySuccess}</span>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-10" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          {/* Embedded in ClientPortalLayout: the layout already shows the title, greeting and progress */}
          {!embedded && (
          <>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="cs-h2">The Figure Co. Portal</h1>
              <p className="cs-meta">
                {portalDisplayName(data) ? `Hi ${portalDisplayName(data)}, please complete the items below` : 'Hello, please complete the items below'}
              </p>
            </div>
            <div className="flex items-center gap-2 cs-meta">
              <Shield className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
              <span>Secure</span>
            </div>
          </div>

          {/* Overall Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="cs-label">Overall Progress</span>
              <span className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>{overallProgress}% Complete</span>
            </div>
            <div className="h-3 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${overallProgress}%`,
                  background: overallProgress === 100 ? 'var(--color-verified)' : 'var(--color-brand)'
                }}
              />
            </div>
          </div>

          </>
          )}

          {/* Tab Navigation */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('overview')}
              className="px-4 py-2 rounded-lg cs-label transition-colors"
              style={
                activeTab === 'overview'
                  ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                  : { color: 'var(--color-text-secondary)', background: 'transparent' }
              }
            >
              Overview
            </button>

            {data?.has_documents && (
              <button
                onClick={() => setActiveTab('documents')}
                data-testid="documents-tab"
                className="px-4 py-2 rounded-lg cs-label transition-colors flex items-center gap-2"
                style={
                  activeTab === 'documents'
                    ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                    : { color: 'var(--color-text-secondary)', background: 'transparent' }
                }
              >
                <FileCheck className="w-4 h-4" />
                Documents
                {docsProgress.total > 0 && (
                  <span
                    className="cs-chip px-2 py-0.5 rounded-full"
                    style={
                      docsProgress.percent === 100
                        ? { background: 'transparent', color: 'var(--color-verified)' }
                        : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                    }
                  >
                    {docsProgress.done}/{docsProgress.total}
                  </span>
                )}
              </button>
            )}

            {data?.has_clarifications && (
              <button
                onClick={() => setActiveTab('clarifications')}
                data-testid="clarifications-tab"
                className="px-4 py-2 rounded-lg cs-label transition-colors flex items-center gap-2"
                style={
                  activeTab === 'clarifications'
                    ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                    : { color: 'var(--color-text-secondary)', background: 'transparent' }
                }
              >
                <ListChecks className="w-4 h-4" />
                Clarifications
                {clarifyProgress.total > 0 && (
                  <span
                    className="cs-chip px-2 py-0.5 rounded-full"
                    style={
                      clarifyProgress.percent === 100
                        ? { background: 'transparent', color: 'var(--color-verified)' }
                        : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                    }
                  >
                    {clarifyProgress.done}/{clarifyProgress.total}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 pb-28">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 rounded-lg p-4 flex items-start gap-3" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
            <div>
              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>Please fix the following:</p>
              <p className="cs-meta mt-1" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              ×
            </button>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            OVERVIEW TAB
        ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="rounded-xl p-5" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-start gap-3">
                <HelpCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                <div className="cs-body">
                  <p className="mb-1" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>What you need to do:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    {data?.has_documents && <li>Upload the requested documents</li>}
                    {data?.has_clarifications && <li>Categorize {data.transactions.length} transaction(s)</li>}
                    <li>Click "Submit All" when done</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Documents Summary Card */}
            {data?.has_documents && (
              <div
                className="rounded-xl p-6 cursor-pointer hover:shadow-md transition-shadow"
                style={{ background: 'var(--color-bg-surface)', border: '2px solid var(--color-needs-human-tint-bd)' }}
                onClick={() => setActiveTab('documents')}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                      <FileCheck className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
                    </div>
                    <div>
                      <h3 className="cs-h3">Documents Needed</h3>
                      <p className="cs-meta">{data.documents_total != null ? data.documents_total : (data.missing_docs?.length || 0)} document(s) requested</p>
                    </div>
                  </div>
                  <div
                    className="px-3 py-1 rounded-full cs-chip"
                    style={
                      docsProgress.percent === 100
                        ? { background: 'transparent', color: 'var(--color-verified)' }
                        : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                    }
                  >
                    {docsProgress.percent === 100 ? '✓ Complete' : `${docsProgress.done} uploaded`}
                  </div>
                </div>

                {data.missing_docs && data.missing_docs.length > 0 && (
                  <div className="space-y-2">
                    {data.missing_docs.slice(0, 3).map((doc, i) => (
                      <div key={i} className="flex items-center gap-2 cs-meta">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--color-brand)' }} />
                        {doc.name}
                      </div>
                    ))}
                    {data.missing_docs.length > 3 && (
                      <p className="cs-meta ml-4">
                        +{data.missing_docs.length - 3} more
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-4 cs-meta" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>
                  Click to upload →
                </div>
              </div>
            )}

            {/* Clarifications Summary Card */}
            {data?.has_clarifications && (
              <div
                className="rounded-xl p-6 cursor-pointer hover:shadow-md transition-shadow"
                style={{ background: 'var(--color-bg-surface)', border: '2px solid var(--color-needs-human-tint-bd)' }}
                onClick={() => setActiveTab('clarifications')}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                      <ListChecks className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
                    </div>
                    <div>
                      <h3 className="cs-h3">Transaction Clarifications</h3>
                      <p className="cs-meta">{data.transactions?.length || 0} transaction(s) need categorization</p>
                    </div>
                  </div>
                  <div
                    className="px-3 py-1 rounded-full cs-chip"
                    style={
                      clarifyProgress.percent === 100
                        ? { background: 'transparent', color: 'var(--color-verified)' }
                        : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                    }
                  >
                    {clarifyProgress.percent === 100 ? '✓ Complete' : `${clarifyProgress.done}/${clarifyProgress.total}`}
                  </div>
                </div>

                {data.transactions && data.transactions.length > 0 && (
                  <div className="space-y-2">
                    {data.transactions.slice(0, 2).map((txn, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg p-2" style={{ background: 'var(--color-bg-elevated)' }}>
                        <div className="flex items-center gap-2">
                          <span className="cs-meta">{txn.date}</span>
                          <span className="cs-body truncate max-w-[200px]" style={{ fontWeight: 500 }}>{txn.description}</span>
                        </div>
                        <span className="cs-figure" style={{ color: txn.amount < 0 ? 'var(--color-text-muted)' : 'var(--color-verified)' }}>
                          {formatCurrency(txn.amount, { cents: true })}
                        </span>
                      </div>
                    ))}
                    {data.transactions.length > 2 && (
                      <p className="cs-meta text-center">
                        +{data.transactions.length - 2} more transactions
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-4 cs-meta" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>
                  Click to categorize →
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            DOCUMENTS TAB - NEW UNIFIED UPLOAD
        ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'documents' && data?.has_documents && (
          <div className="space-y-6">
            {/* Evidence-based Upload Slots (AI-generated dynamic slots) */}
            {USE_EVIDENCE_SLOTS && data.report_id ? (
              <EvidenceUploadSlots
                reportId={data.report_id}
                claimId={data.claim_id}
                draftId={data.draft_id}
                clientName={data.client_name}
                portalToken={token}
                documentsReceived={embedded ? data.documents_received : undefined}
                documentsTotal={embedded ? data.documents_total : undefined}
                documentsComplete={
                  data.documents_total != null
                    ? (data.documents_received ?? 0) >= data.documents_total
                    : undefined
                }
                onUploadComplete={() => {
                  // Refresh portal data when uploads complete (quiet: keeps tab + answers)
                  fetchPortalData(true);
                }}
              />
            ) : (
              <>
                {/* Legacy: Missing Docs List */}
                {data.missing_docs && data.missing_docs.length > 0 && (
                  <div className="rounded-xl p-5" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                    <h3 className="cs-h3 mb-3 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                      <Clock className="w-5 h-5" />
                      Documents Needed ({data.missing_docs.length})
                    </h3>
                    <div className="space-y-2">
                      {data.missing_docs.map((doc, i) => (
                        <div key={i} className="flex items-start gap-3 rounded-lg p-3" style={{ background: 'var(--color-bg-surface)' }}>
                          <div className="w-2 h-2 rounded-full mt-2" style={{ background: 'var(--color-brand)' }} />
                          <div>
                            <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{doc.name}</p>
                            <p className="cs-meta">{doc.reason}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Legacy: Unified Upload Progress */}
                {showUnifiedProgress && unifiedFiles.length > 0 && (
                  <UnifiedUploadProgress
                    files={unifiedFiles}
                    summary={uploadSummary}
                    totalTransactions={totalTransactions}
                    showDetails={true}
                    compact={unifiedFiles.length > 10}
                    onRetry={retryFile}
                  />
                )}

                {/* Legacy: Upload Completion Actions */}
                {allComplete && unifiedFiles.length > 0 && (
                  <div className="rounded-xl p-4" style={{ background: 'transparent', border: '1px solid var(--color-verified)' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
                        <div>
                          <p className="cs-body" style={{ color: 'var(--color-verified)', fontWeight: 600 }}>
                            {completedCount} document{completedCount !== 1 ? 's' : ''} uploaded successfully!
                          </p>
                          <p className="cs-meta" style={{ color: 'var(--color-verified)' }}>
                            Your files are saved and being processed.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={clearAllFiles}
                        className="px-4 py-2 cs-meta rounded-lg transition"
                        style={{ color: 'var(--color-verified)', border: '1px solid var(--color-verified)' }}
                      >
                        Upload More
                      </button>
                    </div>
                  </div>
                )}

                {/* Legacy: Upload Area */}
                {(!showUnifiedProgress || allComplete) && (
                  <div className="rounded-xl shadow-sm p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                    <h3 className="cs-h3 mb-4">Upload Your Documents</h3>

                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      data-testid="document-drop-zone"
                      className="border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer"
                      style={
                        isUploading
                          ? { borderColor: 'var(--color-border)', background: 'var(--color-bg-elevated)', cursor: 'wait', opacity: 0.6 }
                          : isDragging
                            ? { borderColor: 'var(--color-brand)', background: 'var(--color-needs-human-tint)' }
                            : { borderColor: 'var(--color-border)' }
                      }
                    >
                      <CloudUpload className="w-12 h-12 mx-auto mb-4" style={{ color: isDragging ? 'var(--color-brand)' : 'var(--color-text-dim)' }} />
                      <p className="cs-body mb-2">
                        Drag and drop your files here, or
                      </p>
                      <label className="inline-flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition cs-label" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                        <Upload className="w-5 h-5" />
                        Browse Files
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={isUploading}
                      data-testid="file-input"
                    />
                  </label>
                  <p className="cs-meta mt-3">PDF, CSV, Excel • Max {MAX_FILE_SIZE_MB}MB per file • Batch upload supported</p>
                </div>

                {/* Pending Files List - Before upload starts */}
                {pendingFiles.length > 0 && (
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>Selected Files ({pendingFiles.length})</h4>
                      <button
                        onClick={() => setPendingFiles([])}
                        className="cs-meta"
                      >
                        Clear all
                      </button>
                    </div>
                    {pendingFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 rounded-lg"
                        style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                      >
                        {FILE_ICONS[file.type] || <File className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />}
                        <div className="flex-1 min-w-0">
                          <p className="cs-body truncate" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                            {file.name}
                          </p>
                          <p className="cs-meta">
                            {formatFileSize(file.size)} • {guessDocType(file.name)}
                          </p>
                        </div>
                        <button
                          onClick={() => removePendingFile(index)}
                          className="p-1"
                          style={{ color: 'var(--color-text-dim)' }}
                          data-testid={`remove-file-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    <button
                      onClick={uploadAllFiles}
                      disabled={isUploading || pendingFiles.length === 0}
                      data-testid="upload-all-button"
                      className="w-full mt-4 py-3 rounded-lg cs-label disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <CloudUpload className="w-5 h-5" />
                          Upload {pendingFiles.length} Document{pendingFiles.length !== 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Email Fallback */}
            <div className="rounded-xl p-5" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                <h3 className="cs-body" style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>Alternative: Email Documents</h3>
              </div>
              <p className="cs-meta mb-3">
                You can also email documents directly to us.
              </p>
              <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <span className="cs-figure flex-1 truncate" style={{ color: 'var(--color-needs-human-text)' }}>{inboundEmail}</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(inboundEmail);
                    setEmailCopied(true);
                    setTimeout(() => setEmailCopied(false), 2000);
                  }}
                  className="px-3 py-1.5 rounded cs-meta transition"
                  style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                >
                  {emailCopied ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>
              </>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            CLARIFICATIONS TAB
        ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'clarifications' && data?.has_clarifications && (
          <div className="space-y-4">
            {/* Instructions */}
            <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-start gap-3">
                <HelpCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                <div className="cs-body">
                  <p className="mb-1" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>How to categorize:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Review each transaction below</li>
                    <li>Select the most appropriate category</li>
                    <li>Add notes if helpful (optional)</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Transaction List */}
            {data.transactions?.map((txn, index) => {
              const response = responses[txn.transaction_id];
              const isAnswered = !!response?.selected_category;
              const showOtherInput = response?.selected_category === 'Other';
              const showNotes = expandedNotes.has(txn.transaction_id);
              
              return (
                <div
                  key={txn.transaction_id}
                  data-testid={`transaction-card-${index}`}
                  className="rounded-xl transition-all"
                  style={
                    isAnswered
                      ? { background: 'var(--color-bg-surface)', border: '2px solid var(--color-verified)' }
                      : { background: 'var(--color-bg-surface)', border: '2px solid var(--color-border)' }
                  }
                >
                  <div className="p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full cs-chip"
                            style={
                              isAnswered
                                ? { background: 'transparent', color: 'var(--color-verified)' }
                                : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                            }
                          >
                            {isAnswered ? <Check className="w-4 h-4" /> : index + 1}
                          </span>
                          <span className="cs-meta">{txn.date}</span>
                          <span className="cs-figure" style={{ color: txn.amount < 0 ? 'var(--color-text-muted)' : 'var(--color-verified)', fontWeight: 700 }}>
                            {formatCurrency(txn.amount, { cents: true })}
                          </span>
                        </div>
                        <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{txn.description}</p>
                        {txn.current_category && (
                          <p className="cs-meta mt-1">
                            Our guess: <span style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>{txn.current_category}</span>
                          </p>
                        )}
                      </div>

                      {/* AI-Generated Question (more prominent) */}
                      {txn.source === 'qa_assistant' && txn.question && (
                        <div className="rounded-lg px-4 py-3 max-w-md" style={{ background: 'var(--color-needs-human-tint)', borderLeft: '4px solid var(--color-brand)' }}>
                          <div className="flex items-start gap-2">
                            <HelpCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
                            <div>
                              <p className="cs-body mb-1" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>Question from your accountant:</p>
                              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>{txn.question}</p>
                              {txn.ai_suggested_category && (
                                <p className="cs-meta mt-2 flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
                                  <Info className="w-3 h-3" />
                                  Suggested category: <span style={{ fontWeight: 600 }}>{txn.ai_suggested_category}</span>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Regular Question (less prominent) */}
                      {txn.source !== 'qa_assistant' && txn.question && (
                        <div className="rounded-lg px-3 py-2 max-w-xs" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                          <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
                            <span style={{ fontWeight: 500 }}>Question:</span> {txn.question}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Category Selector */}
                    <div className="space-y-3">
                      <label className="block cs-label">
                        Select Category <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
                      </label>
                      <div className="relative">
                        <select
                          data-testid={`category-select-${index}`}
                          value={response?.selected_category || ''}
                          onChange={(e) => handleCategorySelect(txn.transaction_id, e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border-2 appearance-none cursor-pointer transition-colors cs-body"
                          style={
                            isAnswered
                              ? { borderColor: 'var(--color-verified)', background: 'transparent', color: 'var(--color-text-primary)' }
                              : { borderColor: 'var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }
                          }
                        >
                          <option value="">-- Select a category --</option>
                          <optgroup label="Income">
                            {data.categories?.filter(c => c.type === 'income').map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Fixed Costs">
                            {data.categories?.filter(c => c.type === 'fixed').map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Variable Costs">
                            {data.categories?.filter(c => c.type === 'variable').map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Disaster/Insurance Related">
                            {data.categories?.filter(c => c.type === 'extra' || c.type === 'recovery').map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Other">
                            {data.categories?.filter(c => ['one-time', 'non-operating', 'other'].includes(c.type)).map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </optgroup>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none" style={{ color: 'var(--color-text-dim)' }} />
                      </div>

                      {showOtherInput && (
                        <input
                          type="text"
                          placeholder="Enter custom category name..."
                          value={response?.new_category_name || ''}
                          onChange={(e) => handleNewCategoryName(txn.transaction_id, e.target.value)}
                          className="w-full px-4 py-2 rounded-lg border-2 focus:outline-none cs-body"
                          style={{ borderColor: 'var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)', color: 'var(--color-text-primary)' }}
                        />
                      )}

                      {/* Bulk Apply Button */}
                      {isAnswered && similarCounts[txn.transaction_id] > 0 && response?.selected_category !== 'Other' && (
                        <button
                          type="button"
                          onClick={() => openBulkApply(txn, response.selected_category)}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg cs-label transition-colors"
                          style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                        >
                          <Layers className="w-4 h-4" />
                          Apply "{response.selected_category}" to {similarCounts[txn.transaction_id]} similar
                        </button>
                      )}

                      {/* Notes */}
                      <div>
                        <button
                          type="button"
                          onClick={() => toggleNotes(txn.transaction_id)}
                          className="cs-meta flex items-center gap-1"
                          style={{ color: 'var(--color-needs-human-text)' }}
                        >
                          {showNotes ? '− Hide notes' : '+ Add a note (optional)'}
                        </button>
                        {showNotes && (
                          <textarea
                            placeholder="Add any helpful context or notes..."
                            value={response?.notes || ''}
                            onChange={(e) => handleNotesChange(txn.transaction_id, e.target.value)}
                            rows={2}
                            className="mt-2 w-full px-4 py-2 rounded-lg focus:outline-none resize-none cs-body"
                            style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            SUBMIT SECTION (Always visible)
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="mt-8 rounded-xl p-6 sticky bottom-4 shadow-lg" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                {overallProgress === 100
                  ? 'All items complete!'
                  : `${100 - overallProgress}% remaining`
                }
              </p>
              <p className="cs-meta">
                {data?.has_documents && `${docsUploadedShown} doc(s) uploaded`}
                {data?.has_documents && data?.has_clarifications && ' • '}
                {data?.has_clarifications && `${clarifyProgress.done}/${clarifyProgress.total} categorized`}
              </p>
            </div>
            <button
              onClick={handleSubmitAll}
              disabled={submitting || !canSubmit}
              data-testid="submit-all-button"
              className="px-8 py-3 rounded-xl cs-label flex items-center gap-2 transition-all"
              style={
                overallProgress === 100 || canSubmit
                  ? { background: 'var(--color-verified)', color: 'var(--color-bg-surface)' }
                  : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)', cursor: 'not-allowed' }
              }
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit All
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer — deduplicated with ClientPortalLayout when embedded */}
        {!embedded && (
          <div className="mt-6 text-center cs-meta">
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>Link expires in 30 days</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4" />
                <span>Your data is secure</span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
